import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import { ensureFreshAitryxCoreToken } from './aitryxCoreTokenService';
import { ApiResponse, PaginatedResponse } from '../types/partner';
import { config } from '../config/environment';

// Error types
export interface ApiError {
  message: string;
  status?: number;
  code?: string;
  details?: any;
}

// HTTP client configuration
class ApiService {
  private client: AxiosInstance;
  private authToken: string | null = null;

  constructor() {
    this.client = axios.create({
      baseURL: config.api.baseUrl,
      timeout: config.api.timeout,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Optional API logging
    if (config.features.enableApiLogging) {
      this.client.interceptors.request.use(
        (config) => {
          console.log(`API Request: ${config.method?.toUpperCase()} ${config.url}`);
          return config;
        },
        (error) => Promise.reject(error)
      );

      this.client.interceptors.response.use(
        (response) => {
          console.log(`API Response: ${response.status} ${response.config.url}`);
          return response;
        },
        (error) => {
          console.error(`API Error: ${error.response?.status} ${error.config?.url}`);
          return Promise.reject(error);
        }
      );
    }

    // Request interceptor for authentication
    this.client.interceptors.request.use(
      async (config) => {
        // Enterprise-grade security: Use AitryxCore JWT tokens for all API calls
        // This follows the same pattern as customer app for consistent security
        const token = await ensureFreshAitryxCoreToken();
        config.headers.Authorization = `Bearer ${token}`;
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor for error handling
    this.client.interceptors.response.use(
      (response) => response,
      async (error: AxiosError) => {
        const apiError: ApiError = {
          message: 'An unexpected error occurred',
          status: error.response?.status,
        };

        if (error.response?.data) {
          const errorData = error.response.data as any;
          apiError.message = errorData.message || errorData.error || errorData.title || apiError.message;
          apiError.code = errorData.code;
          apiError.details = errorData.details || errorData.errors;
        } else if (error.response) {
          // Response exists but no data - use status text
          apiError.message = `Server error: ${error.response.status} ${error.response.statusText}`;
        } else if (error.code === 'ECONNABORTED' || /timeout/i.test(error.message || '')) {
          apiError.message = 'The request timed out before the server answered.';
        } else if (error.request) {
          apiError.message = 'Network error - please check your connection';
        } else {
          apiError.message = error.message;
        }

        if (error.response?.status === 401 && error.config && !(error.config as any)._retried) {
          try {
            (error.config as any)._retried = true;
            const newToken = await ensureFreshAitryxCoreToken();
            if (!error.config.headers) error.config.headers = {} as any;
            (error.config.headers as any).Authorization = `Bearer ${newToken}`;
            return this.client.request(error.config);
          } catch {
            // fallthrough to error
          }
        }

        return Promise.reject(apiError);
      }
    );
  }

  // Expose client for advanced use-cases (e.g., multipart upload)
  get http() {
    return this.client;
  }

  // Authentication methods
  setAuthToken(token: string): void {
    this.authToken = token;
  }

  clearAuthToken(): void {
    this.authToken = null;
  }

  // Generic API methods
  async get<T>(url: string, params?: any): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.client.get(url, { params });
    return response.data;
  }

  async post<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.client.post(url, data);
    return response.data;
  }

  async put<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.client.put(url, data);
    return response.data;
  }

  async patch<T>(url: string, data?: any): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.client.patch(url, data);
    return response.data;
  }

  async delete<T>(url: string): Promise<ApiResponse<T>> {
    const response: AxiosResponse<ApiResponse<T>> = await this.client.delete(url);
    return response.data;
  }

  async getBlob(url: string): Promise<Blob> {
    const response = await this.client.get(url, { responseType: 'blob' });
    return response.data;
  }

  async getPaginated<T>(url: string, params?: any): Promise<PaginatedResponse<T>> {
    const response: AxiosResponse<PaginatedResponse<T>> = await this.client.get(url, { params });
    return response.data;
  }

  // Health check
  async healthCheck(): Promise<boolean> {
    try {
      await this.get('/health');
      return true;
    } catch {
      return false;
    }
  }
}

// Export singleton instance
export const apiService = new ApiService();
export default apiService; 